import { Camera, MessageSquare, Music2, Send, type LucideIcon } from 'lucide-react';
import { IMAGE_FORMATS_LABEL, PRODUCT_IMAGE_MAX_BYTES } from '@/lib/image-file';
import { formatDate } from '@/lib/format';
import { leadStatus, leadStatusOrder, orderStatus, type ToneName } from '@/theme';
import type {
  AsistenteActivo,
  BotPausaMotivo,
  Channel,
  Direccion,
  EstadoLead,
  OrigenEstadoLead,
  TipoMensaje,
} from '@/types/domain';
import type {
  ConversationFiltros,
  ConversacionResumen,
  MensajeChat,
  MensajeResumen,
  OrdenConversaciones,
  OrdenResumen,
} from '@/types/conversations';

/**
 * Formato de los datos del inbox para la pantalla (funciones puras, sin React: se prueban en el
 * smoke sin navegador).
 *
 * Dos criterios que se repiten en todo el archivo:
 *  - **sin moneda**: el negocio no tiene moneda configurada (ni en `Settings` ni en `Order`), así
 *    que el total de un pedido se pinta como número es-CO y el texto dice de qué es el número;
 *  - **prosa en español con singular/plural explícito** (`describeCount`): "3 mensajes", "1 pedido".
 */

/** Página de la bandeja (`GET .../conversations`, máximo 100 en el backend). */
export const CONVERSACIONES_POR_PAGINA = 30;
/** Página del historial (`GET .../messages`, máximo 200 en el backend). */
export const MENSAJES_POR_PAGINA = 50;
/** Cada cuánto se relee la bandeja mientras el inbox está abierto (los mensajes llegan solos). */
export const INBOX_REFRESH_MS = 30_000;
/** Cuántos caracteres del último mensaje entran en la vista previa de la bandeja. */
export const PREVIEW_MAX_CHARS = 90;

/**
 * Canales que se listan en el panel (chips de filtro de la bandeja).
 *
 * TikTok no está: no se va a usar por ahora y su API no está integrada, así que ofrecer su filtro
 * solo generaba una opción muerta. El tipo `Channel` y `channelMeta` sí lo conservan (el backend lo
 * tiene en su enum y un mensaje viejo con ese canal debe poder pintarse).
 */
export const CHANNELS: Channel[] = ['WHATSAPP', 'INSTAGRAM', 'MESSENGER'];

/**
 * Cómo se llama cada canal en pantalla y con qué se identifica a un contacto.
 *
 * `contactLabel` es el nombre del campo `externalContactId` en ese canal: en WhatsApp es el
 * teléfono (el `wa_id`), en Messenger/Instagram el PSID (que NO es un nombre ni un usuario
 * legible) y en TikTok el `open_id`. La pantalla lo dice para que nadie espere ver un nombre.
 *
 * Los iconos son los que hay en la versión instalada de lucide (no tiene logos de marca).
 */
export const channelMeta: Record<
  Channel,
  { label: string; icon: LucideIcon; tone: ToneName; contactLabel: string }
> = {
  WHATSAPP: {
    label: 'WhatsApp',
    icon: MessageSquare,
    tone: 'success',
    contactLabel: 'Teléfono',
  },
  INSTAGRAM: {
    label: 'Instagram',
    icon: Camera,
    tone: 'warning',
    contactLabel: 'Usuario (PSID)',
  },
  MESSENGER: {
    label: 'Messenger',
    icon: Send,
    tone: 'info',
    contactLabel: 'Usuario (PSID)',
  },
  TIKTOK: {
    label: 'TikTok',
    icon: Music2,
    tone: 'neutral',
    contactLabel: 'Usuario (open_id)',
  },
};

/** Qué asistente del motor está atendiendo la conversación (router del AI Engine). */
export const asistenteMeta: Record<AsistenteActivo, { label: string; tone: ToneName }> = {
  VENTAS: { label: 'Ventas', tone: 'brand' },
  POSTVENTA: { label: 'Postventa', tone: 'info' },
};

/**
 * Quién escribió un mensaje: el contacto o el negocio.
 *
 * `OUT` NO es siempre el bot: desde que el panel deja responder a mano, un saliente puede ser del
 * bot o de una persona del equipo, y `messages` no guarda quién lo escribió (ver *Pendiente* en
 * `docs/INBOX.md`). Por eso la etiqueta es la verdad comprobable —"Bot o equipo"— y no promete un
 * dato que la fila no tiene.
 */
export const direccionLabel: Record<Direccion, string> = {
  IN: 'Cliente',
  OUT: 'Bot o equipo',
};

/** Tipo de mensaje (los mensajes de plantilla son los que Meta cobra y exige aprobar). */
export const tipoMensajeLabel: Record<TipoMensaje, string> = {
  TEXTO: 'Texto',
  IMAGEN: 'Imagen',
  PLANTILLA: 'Plantilla',
  AUDIO: 'Audio',
};

/** Quién fijó el estado del lead: el clasificador o una persona. */
export const leadOriginLabel: Record<OrigenEstadoLead, string> = {
  AUTO: 'Automático',
  MANUAL: 'Manual',
};

/** Estados del lead como opciones de filtro (`''` = sin filtrar). */
export const LEAD_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los estados' },
  ...leadStatusOrder.map((estado) => ({ value: estado, label: leadStatus[estado].label })),
];

/** Canales como opciones de filtro (`''` = sin filtrar). */
export const CHANNEL_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los canales' },
  ...CHANNELS.map((channel) => ({ value: channel, label: channelMeta[channel].label })),
];

/**
 * Filtro de "quién contesta": el handoff es exactamente `botActivo = false` (no hay columna de
 * "requiere atención"), así que este selector se traduce a `botActivo` (`botFilterToFiltro`).
 */
export type BotFilter = 'todos' | 'bot' | 'atencion';

export const BOT_FILTERS: { value: BotFilter; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'bot', label: 'Responde el bot' },
  { value: 'atencion', label: 'Requiere atención' },
];

/** `BotFilter` → `botActivo` del contrato (`undefined` = sin filtro). */
export function botFilterToFiltro(value: BotFilter): boolean | undefined {
  if (value === 'bot') return true;
  if (value === 'atencion') return false;
  return undefined;
}

/** Órdenes del listado, con el nombre que se ve en el selector. */
export const ORDEN_FILTERS: { value: string; label: string }[] = [
  { value: 'actividad', label: 'Última actividad' },
  { value: 'antiguedad', label: 'Más antiguas primero' },
  { value: 'creacion', label: 'Últimas creadas' },
];

/** Estado del bot en la conversación, con el texto que se lee junto al interruptor. */
export const botState = {
  activo: {
    label: 'Bot activo',
    tone: 'brand' as ToneName,
    description: 'El bot contesta solo en esta conversación y sigue el flujo de ventas.',
  },
  pausado: {
    label: 'Bot pausado',
    tone: 'warning' as ToneName,
    description:
      'El bot no enviará nada en esta conversación: contesta el equipo hasta que lo vuelvas a activar.',
  },
};

/** `botActivo` → textos del interruptor de handoff. */
export function botStateMeta(botActivo: boolean): (typeof botState)['activo'] {
  return botActivo ? botState.activo : botState.pausado;
}

/**
 * Por qué está apagado el bot, en una frase (espejo del enum `BotPausaMotivo` del backend).
 *
 * Complementa a `botStateMeta`: hasta ahora el panel solo podía decir «el bot está pausado», sin
 * distinguir un handoff del cliente de un fallo de credenciales de DeepSeek, que se arreglan de forma
 * distinta (el handoff lo atiende una persona; el fallo necesita corregir la key en Integraciones).
 */
export const botPausaMeta: Record<BotPausaMotivo, string> = {
  MANUAL: 'Lo apagó alguien del equipo desde este panel.',
  HANDOFF_CLIENTE: 'El cliente pidió hablar con un asesor.',
  HANDOFF_ORDEN: 'El pedido necesita que lo cierre una persona.',
  DEEPSEEK_CREDENCIALES:
    'Se detuvo solo: DeepSeek rechazó la API key o la cuenta no tiene saldo. Revisa Integraciones → DeepSeek y vuelve a encenderlo.',
};

/**
 * Frase de la pausa («Bot pausado: el cliente pidió hablar con un asesor») o `null` cuando no aplica
 * (bot encendido, o apagado de antes de que existiera la columna).
 */
export function describeBotPausa(
  conversacion: Pick<ConversacionResumen, 'botActivo' | 'botPausadoMotivo'>,
): string | null {
  if (conversacion.botActivo || !conversacion.botPausadoMotivo) return null;
  return `${botState.pausado.label}: ${botPausaMeta[conversacion.botPausadoMotivo]}`;
}

/** "3 mensajes" / "1 pedido" (en español no hay pluralización automática: se dan los dos). */
export function describeCount(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** "25.000" (es-CO, sin símbolo: el negocio no tiene moneda configurada). */
function formatAmount(value: number): string {
  return value.toLocaleString('es-CO', { maximumFractionDigits: 2 });
}

/**
 * Nombre del contacto para mostrar.
 *
 * `nombreSugerido` es el nombre del cliente en su último pedido; si todavía no pidió nada, el
 * único identificador que existe es el del canal (teléfono/PSID).
 */
export function contactName(
  conversacion: Pick<ConversacionResumen, 'nombreSugerido' | 'externalContactId'>,
): string {
  const nombre = conversacion.nombreSugerido?.trim();
  return nombre && nombre.length > 0 ? nombre : conversacion.externalContactId;
}

/** "WhatsApp · Teléfono: 573001234567" — de qué canal viene y cómo se identifica al contacto. */
export function describeContactChannel(
  conversacion: Pick<ConversacionResumen, 'channel' | 'externalContactId'>,
): string {
  const meta = channelMeta[conversacion.channel];
  return `${meta.label} · ${meta.contactLabel}: ${conversacion.externalContactId}`;
}

/**
 * "Hace 5 min", "Hace 3 h", "Ayer", "16 sept 2026".
 *
 * El inbox se refresca solo cada `INBOX_REFRESH_MS`, así que la hora exacta no ayuda: lo que dice
 * si una conversación está viva es cuánto hace de la última actividad. Por eso en la primera
 * semana se cuenta de forma relativa y después se cae a la fecha del panel.
 */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return 'Sin actividad';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diff = Date.now() - date.getTime();
  // Reloj adelantado (o fecha futura): se pinta la fecha en vez de un "en 3 min" que no existe.
  if (diff < 0) return formatDate(value);

  const minutos = Math.floor(diff / 60_000);
  if (minutos < 1) return 'Ahora';
  if (minutos < 60) return `Hace ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;

  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} días`;

  return formatDate(value);
}

/** Todo el texto en una línea (la bandeja no puede crecer de alto por un mensaje largo). */
export function collapseText(text: string, max = PREVIEW_MAX_CHARS): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;

  return `${flat.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Si el contenido es el JSON de un pedido (objeto o array), lo devuelve "bonito" (indentado).
 *
 * El cliente final pega el JSON del pedido en el chat (`docs/INBOX.md`), así que un mensaje puede
 * ser un bloque de 40 líneas. La pantalla lo colapsa en un `<details>`; esta función decide si eso
 * aplica y `null` cubre el texto normal, el JSON inválido y los escalares ("12", '"hola"').
 */
export function prettyJsonMessage(contenido: string): string | null {
  const trimmed = contenido.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return JSON.stringify(parsed, null, 2);
  } catch {
    // Texto que empieza con `{` pero no es JSON (una llave suelta, un emoticono…): es texto.
    return null;
  }
}

/** Cómo se lee un mensaje en la vista previa de la bandeja. */
function describeMensaje(mensaje: MensajeResumen): string {
  if (prettyJsonMessage(mensaje.contenido)) {
    return mensaje.direccion === 'OUT'
      ? 'Envió el resumen del pedido (JSON)'
      : 'Mandó los datos de un pedido (JSON)';
  }

  const text = collapseText(mensaje.contenido);
  if (text.length > 0) return text;

  return `(${tipoMensajeLabel[mensaje.tipo].toLowerCase()})`;
}

/** "Cliente: Hola, ¿tienen talla 43?" — o "Sin mensajes todavía" si la conversación está vacía. */
export function describeLastMessage(
  conversacion: Pick<ConversacionResumen, 'ultimoMensaje'>,
): string {
  const mensaje = conversacion.ultimoMensaje;
  if (!mensaje) return 'Sin mensajes todavía';

  return `${direccionLabel[mensaje.direccion]}: ${describeMensaje(mensaje)}`;
}

/** "3 mensajes · 1 pedido" — lo que lleva la conversación (y lo que el bot ha vendido en ella). */
export function describeConversationStats(
  conversacion: Pick<ConversacionResumen, 'mensajesTotal' | 'ordenesTotal'>,
): string {
  return [
    describeCount(conversacion.mensajesTotal, 'mensaje', 'mensajes'),
    describeCount(conversacion.ordenesTotal, 'pedido', 'pedidos'),
  ].join(' · ');
}

/** Último pedido, tal cual se lee en la bandeja: "Pendiente de confirmación · 25.000". */
export function describeOrdenResumen(orden: OrdenResumen): string {
  return `${orderStatus[orden.estado].label} · ${formatAmount(orden.total)}`;
}

/**
 * "12 conversaciones · 2 requieren atención con el bot pausado · mostrando 30".
 *
 * `total` es el del backend (toda la bandeja con los filtros puestos) y `conversaciones` son las
 * de ESTA página: por eso, cuando son menos que el total, se dice cuántas se están mostrando.
 */
export function summarizeInbox(conversaciones: ConversacionResumen[], total: number): string {
  if (total === 0) {
    return 'Todavía no hay conversaciones: aparecen solas en cuanto alguien escriba por un canal conectado.';
  }

  const atencion = conversaciones.filter((conversacion) => conversacion.requiereAtencionHumana).length;
  const partes = [
    describeCount(total, 'conversación', 'conversaciones'),
    atencion === 0
      ? 'ninguna requiere atención'
      : `${describeCount(atencion, 'requiere', 'requieren')} atención con el bot pausado`,
  ];

  if (conversaciones.length < total) {
    partes.push(`mostrando ${conversaciones.length}`);
  }

  return partes.join(' · ');
}

/** "Mostrando 31–60 de 132" — el rango de la página actual de la bandeja. */
export function describeRange(offset: number, count: number, total: number): string {
  if (count === 0) return 'Ninguna conversación en esta página';

  return `Mostrando ${offset + 1}–${offset + count} de ${total}`;
}

/**
 * Filtros de la PANTALLA (todos string para que los `<select>`/`<input>` los controlen tal cual).
 *
 * Se mantienen separados de `ConversationFiltros` (el contrato) porque no son lo mismo: acá `''`
 * es "sin filtrar" y `bot` es un selector de tres estados, mientras que el contrato quiere
 * `undefined` y `botActivo: boolean`. El puente es `conversationFiltrosFrom` (una función pura).
 */
export interface InboxFiltros {
  /** Búsqueda por el identificador del contacto (el backend busca por `externalContactId`). */
  buscar: string;
  channel: string;
  estadoLead: string;
  bot: BotFilter;
  orden: string;
}

/** Bandeja sin filtrar, ordenada por actividad (lo que se ve al abrir la pantalla). */
export const EMPTY_INBOX_FILTROS: InboxFiltros = {
  buscar: '',
  channel: '',
  estadoLead: '',
  bot: 'todos',
  orden: 'actividad',
};

/** ¿Hay algún filtro puesto? (para ofrecer "Quitar los filtros"). */
export function hasInboxFiltros(filtros: InboxFiltros): boolean {
  return (
    filtros.buscar.trim().length > 0 ||
    filtros.channel !== '' ||
    filtros.estadoLead !== '' ||
    filtros.bot !== 'todos' ||
    filtros.orden !== EMPTY_INBOX_FILTROS.orden
  );
}

/**
 * Filtros de pantalla → filtros del contrato (`limite`/`offset` incluidos).
 *
 * Ojo: cambiar CUALQUIER filtro invalida el `offset` (la pantalla vuelve a la página 0), porque la
 * página 3 de un filtro no tiene nada que ver con la de otro; el `offset` entra por parámetro para
 * que eso se vea en el código que lo llama.
 */
export function conversationFiltrosFrom(
  filtros: InboxFiltros,
  offset: number,
): ConversationFiltros {
  const conversacion: ConversationFiltros = {
    limite: CONVERSACIONES_POR_PAGINA,
    offset,
    orden: filtros.orden as OrdenConversaciones,
  };

  if (filtros.channel) conversacion.channel = filtros.channel as Channel;
  if (filtros.estadoLead) conversacion.estadoLead = filtros.estadoLead as EstadoLead;

  const bot = botFilterToFiltro(filtros.bot);
  if (bot !== undefined) conversacion.botActivo = bot;

  const buscar = filtros.buscar.trim();
  if (buscar) conversacion.buscar = buscar;

  return conversacion;
}

/** Los filtros puestos, en prosa: "canal WhatsApp, estado Caliente y bot pausado". */
export function describeInboxFiltros(filtros: InboxFiltros): string {
  const partes: string[] = [];

  const buscar = filtros.buscar.trim();
  if (buscar) partes.push(`contacto «${buscar}»`);
  if (filtros.channel) partes.push(`canal ${channelMeta[filtros.channel as Channel].label}`);
  if (filtros.estadoLead) {
    partes.push(`estado ${leadStatus[filtros.estadoLead as EstadoLead].label}`);
  }
  if (filtros.bot === 'bot') partes.push('responde el bot');
  if (filtros.bot === 'atencion') partes.push('requiere atención');

  if (partes.length === 0) return 'sin filtros';

  if (partes.length === 1) return partes[0];
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/** "14:05" — la hora suelta de un mensaje (el día lo lleva el separador del bloque). */
export function formatMessageTime(value: string | null | undefined): string {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
}

/** Clave del día LOCAL de un mensaje ("2026-09-16"): para agrupar el chat por jornadas. */
export function messageDayKey(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'sin-fecha';

  const mes = `${date.getMonth() + 1}`.padStart(2, '0');
  const dia = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${mes}-${dia}`;
}

/** "Hoy" / "Ayer" / "16 sept 2026" — el separador entre bloques de mensajes. */
export function describeMessageDay(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';

  // Se comparan días completos (no horas): "ayer" es una jornada, no "hace 24 h".
  const inicioDelDia = (fecha: Date) =>
    new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime();
  const dias = Math.round((inicioDelDia(new Date()) - inicioDelDia(date)) / 86_400_000);

  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';

  return formatDate(value);
}

/** Bloque de mensajes de un mismo día, con su etiqueta de separador. */
export interface BloqueMensajes {
  /** Clave del día (el `key` de React). */
  key: string;
  /** "Hoy" / "Ayer" / "16 sept 2026". */
  label: string;
  mensajes: MensajeChat[];
}

/**
 * Agrupa el historial (que viene ascendente) por día local.
 *
 * El chat se lee por jornadas: sin el separador, un historial de semanas es una lista de burbujas
 * sin referencia temporal. Se conserva el orden de entrada, así que los bloques también salen
 * cronológicos.
 */
export function groupMessagesByDay(mensajes: MensajeChat[]): BloqueMensajes[] {
  const bloques: BloqueMensajes[] = [];

  for (const mensaje of mensajes) {
    const key = messageDayKey(mensaje.createdAt);
    const ultimo = bloques[bloques.length - 1];

    if (ultimo && ultimo.key === key) {
      ultimo.mensajes.push(mensaje);
      continue;
    }

    bloques.push({ key, label: describeMessageDay(mensaje.createdAt), mensajes: [mensaje] });
  }

  return bloques;
}

/* -------------------------------------------------------------------------- */
/* Composer: responder a mano desde el panel                                  */
/* -------------------------------------------------------------------------- */

/**
 * Tope de caracteres del mensaje que se escribe a mano.
 *
 * Es el MISMO valor que valida el backend (`MENSAJE_MAX_CHARS` en
 * `conversations.schemas.ts`, el límite de WhatsApp Cloud API para el cuerpo de un texto). Se
 * repite aquí a propósito: el `maxLength` del campo evita que el usuario escriba algo que va a
 * recibir un 400, y el smoke comprueba que los dos números no se separaron.
 */
export const MENSAJE_MAX_CHARS = 4096;

/** A partir de cuántos caracteres restantes se enseña el contador (antes sería ruido). */
export const MENSAJE_AVISO_RESTANTE = 500;

/**
 * Tope de la foto que se manda desde el chat: el MISMO que las fotos del catálogo
 * (`PRODUCT_IMAGE_MAX_BYTES` del frontend = `MAX_PRODUCT_IMAGE_BYTES` del backend, 2 MB), y no el de
 * WhatsApp (5 MB): el panel reescala a 1600 px antes de subirla y el archivo se queda en el disco del
 * servidor (`uploads/chat/`).
 */
export const MENSAJE_IMAGEN_MAX_BYTES = PRODUCT_IMAGE_MAX_BYTES;

/** Tope del pie de foto: espejo de `MENSAJE_CAPTION_MAX` del backend (el tope de Meta para captions). */
export const MENSAJE_CAPTION_MAX = 1024;

/** Textos del composer, en un solo sitio (el smoke los comprueba sin navegador). */
export const composerText = {
  placeholder: 'Escribe un mensaje…',
  enviar: 'Enviar',
  /** Lo que NO se puede deducir de la pantalla: a dónde va el texto y cuándo Meta lo rechaza. */
  aviso: 'Se envía al WhatsApp del contacto y queda en el historial. Meta solo acepta texto libre hasta 24 h después de su último mensaje; pasado ese tiempo hay que usar una plantilla.',
  ayudaTeclado: 'Enter envía · Shift + Enter salta de línea',
  adjuntar: 'Adjuntar una foto',
  quitarFoto: 'Quitar la foto',
  placeholderPie: 'Escribe un pie de foto (opcional)…',
  /** Aviso cuando hay una foto adjunta (el de texto habla de texto libre, que aquí no aplica). */
  avisoFoto:
    'La foto se envía al WhatsApp del contacto y queda en el historial. Vale dentro de la ventana de 24 h, igual que el texto.',
  /** Ayuda del adjunto: qué se puede mandar y con qué tope. */
  fotoAyuda: `Se manda como imagen de WhatsApp (${IMAGE_FORMATS_LABEL}, hasta ${Math.round(MENSAJE_IMAGEN_MAX_BYTES / 1024 / 1024)} MB) y el texto de arriba viaja como pie de foto.`,
} as const;

/** El texto tal como se va a enviar: sin espacios de sobra al principio ni al final. */
export function normalizeMensaje(contenido: string): string {
  return contenido.trim();
}

/** ¿Se puede enviar? (no vacío y dentro del tope de caracteres). */
export function puedeEnviarMensaje(contenido: string): boolean {
  const texto = normalizeMensaje(contenido);
  return texto.length > 0 && texto.length <= MENSAJE_MAX_CHARS;
}

/** Cuántos caracteres quedan antes del tope (`0` = ya no cabe ni uno más). */
export function restantesDeMensaje(contenido: string): number {
  return Math.max(MENSAJE_MAX_CHARS - normalizeMensaje(contenido).length, 0);
}

/** ¿Toca enseñar el contador? Solo cuando queda poco para el tope. */
export function mostrarContadorDeMensaje(contenido: string): boolean {
  return restantesDeMensaje(contenido) <= MENSAJE_AVISO_RESTANTE;
}

/** "4096/4096" — el contador del composer. */
export function describeMensajeLength(contenido: string): string {
  return `${normalizeMensaje(contenido).length}/${MENSAJE_MAX_CHARS}`;
}

/* -------------------------------------------------------------------------- */
/* Composer: la FOTO adjunta                                                  */
/* -------------------------------------------------------------------------- */

/** Texto de la burbuja cuando una foto no se puede previsualizar (URL caducada o rota). */
export const FOTO_SIN_PREVISUALIZAR = 'Foto (no se pudo previsualizar)';

/* -------------------------------------------------------------------------- */
/* Entrega de los mensajes salientes (módulo 5)                                */
/* -------------------------------------------------------------------------- */

/** Textos del aviso de entrega de la burbuja (el smoke los comprueba sin navegador). */
export const entregaText = {
  noEntregado: 'No se entregó',
  reintentar: 'Reintentar',
  /**
   * Se añade cuando el rechazo es la ventana de 24 h: reintentar el mismo texto va a fallar otra vez,
   * así que el aviso dice qué SÍ funciona (una plantilla aprobada, módulo 8).
   */
  usarPlantilla: 'Pasaron más de 24 h desde su último mensaje: hay que mandar una plantilla aprobada.',
} as const;

/**
 * ¿Este mensaje se intentó enviar y el canal lo rechazó?
 *
 * Se exige `entregado === false` (no un `null`): los mensajes anteriores a la columna y todos los
 * entrantes llegan sin dato, y de ellos no se puede afirmar que no se entregaran.
 */
export function mensajeNoEntregado(mensaje: Pick<MensajeChat, 'direccion' | 'entregado'>): boolean {
  return mensaje.direccion === 'OUT' && mensaje.entregado === false;
}

/**
 * ¿El rechazo fue por la ventana de 24 h de WhatsApp?
 *
 * Meta lo reporta con el código `131047` y el texto «more than 24 hours». Mismo criterio que
 * `isOutOfWindowError` del backend, pero sobre el motivo ya guardado en la fila.
 */
export function errorDeVentana(errorEnvio: string | null): boolean {
  const texto = (errorEnvio ?? '').toLowerCase();
  return texto.includes('131047') || texto.includes('24 hours') || texto.includes('24 h');
}

/** Motivo del rechazo listo para pintar, o `null` si el canal no dijo nada. */
export function describeErrorEnvio(errorEnvio: string | null): string | null {
  const texto = (errorEnvio ?? '').trim();
  return texto.length > 0 ? texto : null;
}

/**
 * ¿El contenido de un mensaje es una URL de imagen?
 *
 * Vale para las dos formas que guarda el backend: una URL absoluta (una foto pegada por URL o un CDN)
 * y la ruta relativa `/uploads/…` de las fotos propias. Lo demás (incluido el literal
 * «(mensaje sin texto)») no se intenta pintar como imagen.
 */
export function esUrlDeImagen(contenido: string): boolean {
  const value = contenido.trim();
  return /^https?:\/\//i.test(value) || value.startsWith('/');
}

/** ¿Cabe el pie de foto en el tope de Meta? (el campo lo corta igual con `maxLength`). */
export function puedeEnviarCaption(caption: string): boolean {
  return normalizeMensaje(caption).length <= MENSAJE_CAPTION_MAX;
}

/** "132/1024" — el contador del pie de foto (se enseña siempre: es un campo corto). */
export function describeCaptionLength(caption: string): string {
  return `${normalizeMensaje(caption).length}/${MENSAJE_CAPTION_MAX}`;
}
